import React, { useCallback, useEffect, useState, useMemo, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';

import { useNavigate } from 'react-router';
import classNames from 'classnames';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CloseDialogBtn,
  CtaBtn,
  CustomSelect,
  DangerAlert,
  Dialog,
  FormItem,
  FormRow,
  InfoBadge,
  Input,
  SecondaryBtn,
  SecondaryLink,
  SvgExternal,
  SvgPen,
  TertiaryBtn,
  Textarea,
  WaitingAlert,
  WarningAlert,
} from '@tmapy/style-guide';

import type { DocRetrieveResponse, UserListResponse } from '../../openapi/types';
import { useDocsRetrieveResource } from '../../openapi/operations/docsRetrieve';
import { docsGetRozsahAktualizacniZmenyExtentPartialUpdate } from '../../openapi/operations/docsGetRozsahAktualizacniZmenyExtentPartialUpdate';
import { resolveMapUrl } from '../utils/resolveMapUrl';
import { MapReviewButtons } from './MapReviewButtons';

// dmvsGad
import { dmvsGadDocsClosePartialUpdate } from '../../openapi/operations/dmvsGadDocsClosePartialUpdate';
import { dmvsGadDocsRejectPartialUpdate } from '../../openapi/operations/dmvsGadDocsRejectPartialUpdate';
import { dmvsGadDocsImportDocCreate } from '../../openapi/operations/dmvsGadDocsImportDocCreate';
import { dmvsGadDocsCloseInterruptPartialUpdate } from '../../openapi/operations/dmvsGadDocsCloseInterruptPartialUpdate';
import { dmvsGadDocsImportInterruptPartialUpdate } from '../../openapi/operations/dmvsGadDocsImportInterruptPartialUpdate';
import { dmvsGadDocsNotifyClosePartialUpdate } from '../../openapi/operations/dmvsGadDocsNotifyClosePartialUpdate';
import { dmvsGadDocsEditPartialUpdate } from '../../openapi/operations/dmvsGadDocsEditPartialUpdate';
import { dmvsGadDocsEditCheckCreate } from '../../openapi/operations/dmvsGadDocsEditCheckCreate';
import { dmvsGadDocsPartialUpdate } from '../../openapi/operations/dmvsGadDocsPartialUpdate';
import { dmvsGadDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/dmvsGadDocsR17PotvrdZapracovatelnostCreate';
import { dmvsGadDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/dmvsGadDocsCheckDmvsPendingPozadavkyCreate';
// crossOwner
import { crossOwnerDocsEditPartialUpdate } from '../../openapi/operations/crossOwnerDocsEditPartialUpdate';
import { crossOwnerDocsSendCloseDataToDmvsPartialUpdate } from '../../openapi/operations/crossOwnerDocsSendCloseDataToDmvsPartialUpdate';
import { crossOwnerDocsSynchronizeCreate } from '../../openapi/operations/crossOwnerDocsSynchronizeCreate';
import { crossOwnerDocsClosePartialUpdate } from '../../openapi/operations/crossOwnerDocsClosePartialUpdate';
import { crossOwnerDocsRequestSynchronizationDataCreate } from '../../openapi/operations/crossOwnerDocsRequestSynchronizationDataCreate';
import { crossOwnerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/crossOwnerDocsSendToCounterpartPartialUpdate';
import { crossOwnerDocsRejectPartialUpdate } from '../../openapi/operations/crossOwnerDocsRejectPartialUpdate';
import { crossOwnerDocsCloseInterruptPartialUpdate } from '../../openapi/operations/crossOwnerDocsCloseInterruptPartialUpdate';
import { crossOwnerDocsImportInterruptPartialUpdate } from '../../openapi/operations/crossOwnerDocsImportInterruptPartialUpdate';
import { crossOwnerDocsNotifyClosePartialUpdate } from '../../openapi/operations/crossOwnerDocsNotifyClosePartialUpdate';
import { crossOwnerDocsEditCheckCreate } from '../../openapi/operations/crossOwnerDocsEditCheckCreate';
import { crossOwnerDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/crossOwnerDocsCheckDmvsPendingPozadavkyCreate';
import { crossOwnerDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/crossOwnerDocsR17PotvrdZapracovatelnostCreate';
// crossWorker
import { crossWorkerDocsSynchronizeCreate } from '../../openapi/operations/crossWorkerDocsSynchronizeCreate';
import { crossWorkerDocsRequestSynchronizationDataCreate } from '../../openapi/operations/crossWorkerDocsRequestSynchronizationDataCreate';
import { crossWorkerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/crossWorkerDocsSendToCounterpartPartialUpdate';
import { crossWorkerDocsClosePartialUpdate } from '../../openapi/operations/crossWorkerDocsClosePartialUpdate';
import { crossWorkerDocsRejectPartialUpdate } from '../../openapi/operations/crossWorkerDocsRejectPartialUpdate';
import { crossWorkerDocsImportDocCreate } from '../../openapi/operations/crossWorkerDocsImportDocCreate';
// svuOwner
import { svuOwnerDocsDelegatePartialUpdate } from '../../openapi/operations/svuOwnerDocsDelegatePartialUpdate';
import { svuOwnerDocsClosePartialUpdate } from '../../openapi/operations/svuOwnerDocsClosePartialUpdate';
import { svuOwnerDocsEditPartialUpdate } from '../../openapi/operations/svuOwnerDocsEditPartialUpdate';
import { svuOwnerDocsSendCloseDataToDmvsPartialUpdate } from '../../openapi/operations/svuOwnerDocsSendCloseDataToDmvsPartialUpdate';
import { svuOwnerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/svuOwnerDocsSendToCounterpartPartialUpdate';
import { svuOwnerDocsRejectPartialUpdate } from '../../openapi/operations/svuOwnerDocsRejectPartialUpdate';
import { svuOwnerDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/svuOwnerDocsR17PotvrdZapracovatelnostCreate';
// svuWorker
import { svuWorkerDocsCheckSvuCrossKrajPartialUpdate } from '../../openapi/operations/svuWorkerDocsCheckSvuCrossKrajPartialUpdate';
import { svuWorkerDocsImportDocCreate } from '../../openapi/operations/svuWorkerDocsImportDocCreate';
import { svuWorkerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/svuWorkerDocsSendToCounterpartPartialUpdate';
import { svuWorkerDocsDelegatePartialUpdate } from '../../openapi/operations/svuWorkerDocsDelegatePartialUpdate';
import { svuWorkerDocsClosePartialUpdate } from '../../openapi/operations/svuWorkerDocsClosePartialUpdate';
import { svuWorkerDocsRejectPartialUpdate } from '../../openapi/operations/svuWorkerDocsRejectPartialUpdate';
import { svuWorkerDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/svuWorkerDocsR17PotvrdZapracovatelnostCreate';
// svuCrossOwner
import { svuCrossOwnerDocsEditPartialUpdate } from '../../openapi/operations/svuCrossOwnerDocsEditPartialUpdate';
import { svuCrossOwnerDocsSendCloseDataToDmvsPartialUpdate } from '../../openapi/operations/svuCrossOwnerDocsSendCloseDataToDmvsPartialUpdate';
import { svuCrossOwnerDocsSynchronizeCreate } from '../../openapi/operations/svuCrossOwnerDocsSynchronizeCreate';
import { svuCrossOwnerDocsRejectPartialUpdate } from '../../openapi/operations/svuCrossOwnerDocsRejectPartialUpdate';
import { svuCrossOwnerDocsClosePartialUpdate } from '../../openapi/operations/svuCrossOwnerDocsClosePartialUpdate';
import { svuCrossOwnerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/svuCrossOwnerDocsSendToCounterpartPartialUpdate';
import { svuCrossOwnerDocsRequestSynchronizationDataCreate } from '../../openapi/operations/svuCrossOwnerDocsRequestSynchronizationDataCreate';
import { svuCrossOwnerDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/svuCrossOwnerDocsR17PotvrdZapracovatelnostCreate';
// svuCrossWorker
import { svuCrossWorkerDocsImportDocCreate } from '../../openapi/operations/svuCrossWorkerDocsImportDocCreate';
import { svuCrossWorkerDocsRejectPartialUpdate } from '../../openapi/operations/svuCrossWorkerDocsRejectPartialUpdate';
import { svuCrossWorkerDocsClosePartialUpdate } from '../../openapi/operations/svuCrossWorkerDocsClosePartialUpdate';
import { svuCrossWorkerDocsSendToCounterpartPartialUpdate } from '../../openapi/operations/svuCrossWorkerDocsSendToCounterpartPartialUpdate';
import { svuCrossWorkerDocsRequestSynchronizationDataCreate } from '../../openapi/operations/svuCrossWorkerDocsRequestSynchronizationDataCreate';
import { svuCrossWorkerDocsR17PotvrdZapracovatelnostCreate } from '../../openapi/operations/svuCrossWorkerDocsR17PotvrdZapracovatelnostCreate';

import { docsList } from '../../openapi/operations/docsList';
import { usersList } from '../../openapi/operations/usersList';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { DocStatesEnum } from '../../openapi/types/DocStatesEnum';

import { RouteId, routeToUrl, RouteUrlGetterParams } from '../../routes';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { MapWrapper } from '../../app/components/MapWrapper';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { LogList } from '../../logs/components/LogList';
import { mapConfig } from '../../manager/mapConfig';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useInput,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { APP_TYPE, QUERY_STATE } from '../../constants';
import type { ApiAction } from '../../types';

import {
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
  DocFormTypes,
  OWNER_FLOWS,
  usableItemsForFormType,
  WORKER_FLOWS,
} from '../constants';

import { getFormValues } from '../utils/getformvalues';
import {
  docFormMessages,
  docTitleMessages,
  rejectionMessages,
  rejectionReasonMessages,
} from './docsMessages';
import { docEditButtonsMsg, docEditMessages } from './docEditMessages';
import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { Rejection } from './Rejection';
import { Delegate } from './Delegate';
import { CheckRecordList } from './CheckRecordList';
import { LogAlert } from './LogAlert';
import { LinkTooltip } from './LinkTooltip';
import { DocDownloadFiles } from './DocDownloadFiles';
import { DocInternalInfo } from './DocInternalInfo';
import { HistoryGraph } from './HistoryGraph';
import { getErrorMessages, ResponseErrorMessage } from './ResponseErrorMessage';
import { useMonitoringTimeline } from '../../monitoring/hooks/useMonitoringTimeline';
import { HistoryList } from '../../history/components/HistoryList';

import './Doc.scss';

const SEND_TO_COUNTERPART_NOT_CHECKED = 'send_to_counterpart_not_checked';
const SEND_TO_DMVS_CONFLICT = 'send_to_dmvs_conflict';

// Server vrací u těchto kódů anglickou hlášku, text se proto vybírá podle kódu.
const getSendToCounterpartFailMessage = (code?: string) => {
  switch (code) {
    case SEND_TO_COUNTERPART_NOT_CHECKED:
      return docEditMessages.sendToCounterpartNotCheckedFail;
    case SEND_TO_DMVS_CONFLICT:
      return docEditMessages.sendToDmvsConflictFail;
    default:
      return docEditMessages.crossDataToDmvsQueryStateFail;
  }
};

type DocActionQuery = { id?: number; svu_id_subjektu?: string };

export type DocEditContainerProps = {
  docId?: number;
};

const renderUserSelectItem = (item: UserListResponse) => (
  <>
    {item.name} {item.surname} ({item.username})
  </>
);

const getAziValue = (data: DocRetrieveResponse) => {
  if (data.azi_cislo_opravneni === null) return '';

  if (data.geodesist?.name || data.geodesist?.surname) {
    return `${data.azi_cislo_opravneni} (${data.geodesist.name || ''} ${data.geodesist.surname || ''})`.trim();
  }

  return `${data.azi_cislo_opravneni}`;
};

export const DocDmvsGad: React.FC<DocEditContainerProps> = ({ docId }) => {
  const hasHistoryGraph = useSelector(
    (state) => state.app.config.manager?.modules?.historyGraph ?? false,
  );

  const monitoringSubject = useMemo(
    () => (docId && hasHistoryGraph ? { doc: docId } : null),
    [docId, hasHistoryGraph],
  );
  const timeline = useMonitoringTimeline(monitoringSubject, {
    withErrors: true,
    withWarnings: true,
  });

  useSSE(SSE_TYPE.DOCS, docId, timeline.markStale);
  const auth = useAuth();

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reviewerConfigUrl = useSelector((state) => state.app.config.reviewer.url);
  const reviewer3dConfigUrl = useSelector((state) => state.app.config.reviewer.url3d);
  const editorConfigUrl = useSelector((state) => state.app.config.editorZPS.url);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const [checkLogId, setCheckLogId] = useState<number | undefined>(undefined);
  const [isLogDialogShown, showLogDialog] = useState(false);

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkQueryState, setCheckQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkSvuCrossKrajQueryState, setCheckSvuCrossKrajQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [potvrdZapracovatelnostQueryState, setPotvrdZapracovatelnostQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeInterruptQueryState, setCloseInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [importInterruptQueryState, setImportInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [notifyCloseQueryState, setNotifyCloseQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [editQueryState, setEditQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [crossEditQueryState, setCrossEditQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [crossDataToDmvsQueryState, setCrossDataToDmvsQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [sendToCounterpartError, setSendToCounterpartError] = useState<{
    code?: string;
    message?: string;
  } | null>(null);
  const [sendCloseDataErrors, setSendCloseDataErrors] = useState<
    { code: string; message: string }[] | null
  >(null);
  const [synchronizationQueryState, setSynchronizationQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [checkDmvsPendingPozadavkyQueryState, setCheckDmvsPendingPozadavkyQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const [isRejectionDialogShown, showRejectionDialog] = useState(false);
  const [isDelegateSvuDialogShown, showDelegateSvuDialog] = useState(false);

  const stateData = useSelector((state) => state.doc.data);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData ? cachedData : { ...defaultValues };
  }, [resource.data, stateData, docId]);

  const [mapDataExtent, setMapDataExtent] = useState<number[]>([]);
  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const mapExtent = useMemo(() => {
    return resource.data?.rozsah_aktualizacni_zmeny_extent.length
      ? resource.data.rozsah_aktualizacni_zmeny_extent
      : mapDataExtent.length
        ? mapDataExtent
        : undefined;
  }, [resource.data?.rozsah_aktualizacni_zmeny_extent, mapDataExtent]);

  const formType: DocFormTypes = 'dmvsGad';
  const usableItems = usableItemsForFormType[formType];

  const aclSchemaIdent = useMemo(() => {
    switch (data.flow) {
      case FlowEnum.SVU_OWNER:
        return 'SvuOwnerDocs';
      case FlowEnum.SVU_WORKER:
        return 'SvuWorkerDocs';
      case FlowEnum.CROSS_OWNER:
        return 'CrossOwnerDocs';
      case FlowEnum.CROSS_WORKER:
        return 'CrossWorkerDocs';
      case FlowEnum.SVU_CROSS_OWNER:
        return 'SvuCrossOwnerDocs';
      case FlowEnum.SVU_CROSS_WORKER:
        return 'SvuCrossWorkerDocs';
      default:
        return 'DmvsGadDocs';
    }
  }, [data.flow]);

  const acl = useMemo(
    () => ({
      docsActionNotifyClose: auth.hasPermission(aclSchemaIdent, 'action.notify_close', data),
      docsActionImportInterrupt: auth.hasPermission(
        aclSchemaIdent,
        'action.import_interrupt',
        data,
      ),
      docsActionCloseInterrupt: auth.hasPermission(aclSchemaIdent, 'action.close_interrupt', data),
      docsActionClose: auth.hasPermission(aclSchemaIdent, 'action.close', data),
      docsActionReject: auth.hasPermission(aclSchemaIdent, 'action.reject', data),
      docsActionEdit: auth.hasPermission(aclSchemaIdent, 'action.edit', data),
      docsActionImport: auth.hasPermission(aclSchemaIdent, 'action.import_doc', data),
      docsActionShowLog: auth.hasPermission(aclSchemaIdent, 'action.logs', data),
      docsActionMapReview: auth.hasPermission(aclSchemaIdent, 'action.map_review', data),
      docsActionMapEdit: auth.hasPermission(aclSchemaIdent, 'action.map_edit', data),
      docsActionEditCheck: auth.hasPermission(aclSchemaIdent, 'action.edit_check', data),
      docsActionChecksVisibility: auth.hasPermission(aclSchemaIdent, 'checks_visibility', data),
      docsActionSynchronize: auth.hasPermission(aclSchemaIdent, 'action.synchronize', data),
      docsActionCheckSvuCrossKraj: auth.hasPermission(aclSchemaIdent, 'check_svu_cross_kraj', data),
      docsActionRequestSynchronizationData: auth.hasPermission(
        aclSchemaIdent,
        'action.request_synchronization_data',
        data,
      ),
      docsActionSendToCounterpart: auth.hasPermission(
        aclSchemaIdent,
        'action.send_to_counterpart',
        data,
      ),
      docsActionCloseCrossDataToDmvs: auth.hasPermission(
        aclSchemaIdent,
        'action.send_close_cross_data_to_dmvs',
        data,
      ),
      docsActionPotvrdZapracovatelnost: auth.hasPermission(
        aclSchemaIdent,
        'r17_potvrd_zapracovatelnost',
        data,
      ),
      // Předat informaci o uzavření -> action.send_close_cross_data_to_dmvs
      docsActionDelegate: auth.hasPermission(aclSchemaIdent, 'action.delegate', data),
      /**
       * polozka, ktera i pri disable formulare ve stavu DTM_EDITING muze byt zmenena a ulozena
       * ovlivnuje tedy viditelnost formularoveho prvku
       */
      docsUpdateEditorManager: auth.hasPermission(aclSchemaIdent, 'update.editor_manager', data),

      /**
       *
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission(aclSchemaIdent, 'partial_update', data),
      docsUpdate: auth.hasPermission(aclSchemaIdent, 'update', data),

      // download button section
      docsActionDownloadProtocol: auth.hasPermission(aclSchemaIdent, 'download_r17_protocol', data),
      docsActionDownloadSynchronizedLog: auth.hasPermission(
        aclSchemaIdent,
        'download_synchronized_log',
        data,
      ),
      docsActionDownloadCloseLog: auth.hasPermission(aclSchemaIdent, 'download_close_log', data),
      docsActionDownloadImportLog: auth.hasPermission(aclSchemaIdent, 'download_import_log', data),
      docsActionDownloadDmvsLog: auth.hasPermission(aclSchemaIdent, 'download_dmvs_log', data),

      // restart dmvs pending
      docsActionCheckDmvsPendingPozadavky: auth.hasPermission(
        aclSchemaIdent,
        'check_dmvs_pending_pozadavky',
        data,
      ),
    }),
    [auth, data, aclSchemaIdent],
  );
  const showChecks = usableItems?.includes('checks') && acl.docsActionChecksVisibility;

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const closeLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_close_log',
    handleShowDownloadLogError,
  );

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  const protocolDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_r17_protocol',
    handleShowDownloadLogError,
  );

  const digitalniOsvedceniDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_digitalni_osvedceni',
    handleShowDownloadLogError,
  );

  const logSynchronizedDonwloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_synchronized_log',
    handleShowDownloadLogError,
  );

  const dmvsLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_dmvs_log',
    handleShowDownloadLogError,
  );

  // MAP
  useEffect(() => {
    (async () => {
      if (data.rozsah_aktualizacni_zmeny_extent.length) {
        setMapDataExtent(data.rozsah_aktualizacni_zmeny_extent);
        return;
      }

      if (docId && (acl.docsActionMapReview || acl.docsActionMapEdit)) {
        const result = await docsGetRozsahAktualizacniZmenyExtentPartialUpdate({
          query: { id: docId },
          request: {},
        });
        if (result.ok && result.data?.rozsah_aktualizacni_zmeny_extent) {
          setMapDataExtent(result.data.rozsah_aktualizacni_zmeny_extent);
        } else {
          console.warn('Nepodařilo se získat rozsah aktualizační změny.', result.error.data);
        }
      }
    })();
  }, [
    docId,
    data.rozsah_aktualizacni_zmeny_extent,
    acl.docsActionMapReview,
    acl.docsActionMapEdit,
  ]);

  const hashParams = useMemo(
    () => (mapDataExtent?.length ? `#bbox=${mapDataExtent}` : ''),
    [mapDataExtent],
  );

  // MAP REVIEWER
  const canMapReview = !!docId && acl.docsActionMapReview;

  const reviewUrl = useMemo(
    () => resolveMapUrl(reviewerConfigUrl, `-zps/${docId}/${hashParams}`),
    [reviewerConfigUrl, docId, hashParams],
  );
  const review3dUrl = useMemo(
    () => resolveMapUrl(reviewer3dConfigUrl, `-zps/?doc_id=${docId}`),
    [reviewer3dConfigUrl, docId],
  );

  // MAP EDITOR
  const isUserEditor = useMemo(
    () => data.editor_manager?.id === auth.id,
    [data.editor_manager, auth.id],
  );
  const editorUrl = useMemo(
    () => resolveMapUrl(editorConfigUrl, `/${docId}/${hashParams}`),
    [editorConfigUrl, docId, hashParams],
  );

  const isActionMapEditVisible = useMemo(
    () => !!docId && acl.docsActionMapEdit && editorUrl,
    [editorUrl, acl.docsActionMapEdit, docId],
  );

  // LINK TO DTMK KAD
  const [linkToDtmkKadWarningShow, setLinkToDtmkKadWarningShow] = useState(false);
  const handleLinkToDtmkKadBtnClick = useCallback(async () => {
    if (!data.description || !data.dmvs_identifikace_zmeny) return;

    const baseQuery = { id: Number(data.dmvs_identifikace_zmeny) };

    const [byName, byDescription] = await Promise.all([
      docsList({ query: { ...baseQuery, name__icontains: data.description } }),
      docsList({ query: { ...baseQuery, description__icontains: data.description } }),
    ]);

    const result = byDescription.data?.results?.[0] ?? byName.data?.results?.[0];

    if (result) {
      const getUrl = routeToUrl['podaniKADDetail'] as (
        params?: RouteUrlGetterParams<RouteId>,
      ) => string;
      navigate(getUrl({ appType: APP_TYPE.MANAGER, id: result.id }));
    } else {
      setLinkToDtmkKadWarningShow(true);
    }
  }, [data.description, data.dmvs_identifikace_zmeny, navigate]);

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
  }, [setSaveQueryState]);

  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani',
    isDisabled: true,
    initialValue: data.dmvs_id_podani,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });
  const dmvs_source = useInput<InputProps>({
    name: 'dmvs_source',
    isDisabled: true,
    initialValue: data.dmvs_source,
    label: formatMessage(docFormMessages.dmvs_source),
  });
  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: true,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
  });
  const dmvs_identifikace_zmeny = useInput<InputProps>({
    name: 'dmvs_identifikace_zmeny',
    isDisabled: true,
    initialValue: data.dmvs_identifikace_zmeny,
    label: formatMessage(docFormMessages.dmvs_identifikace_zmeny),
  });
  const azi_cislo_opravneni = useInput<InputProps>({
    name: 'azi_cislo_opravneni',
    isDisabled: true,
    initialValue: getAziValue(data),
    label: formatMessage(docFormMessages.azi_cislo_opravneni),
  });
  const subjekt_jmeno = useInput<InputProps>({
    name: 'subjekt_jmeno',
    isDisabled: true,
    initialValue: data.subjekt?.jmeno,
    label: formatMessage(docFormMessages.subjekt_jmeno),
  });
  const geodesist = useInput<InputProps>({
    name: 'geodesist',
    isDisabled: true,
    initialValue: `${data.geodesist?.name || ''} ${data.geodesist?.surname || ''}`,
    label: formatMessage(docFormMessages.geodesist),
  });

  const prepareEditorManagerItems = useCallback(async (search: string) => {
    const response = await usersList({
      query: { search, roles: ['ADMIN'], is_active: true, is_editor: true, page_size: 50 },
    });
    if (response.ok) {
      return response.data?.results;
    }
    return null;
  }, []);
  const editor_manager = useSelect({
    name: 'editor_manager',
    isDisabled: !acl.canUpdate,
    label: formatMessage(docFormMessages.editor_manager),
    initialValue: data.editor_manager,
    onChange: formItemChangeCallback,
    prepareItems: prepareEditorManagerItems,
    renderValue: (item) =>
      item?.name || item?.surname
        ? `${[item.name, item.surname].join(' ')} (${item.username})`
        : '',
    renderItem: renderUserSelectItem,
    ...formItemServerErrors.editor_manager,
  });

  // DIALOGS
  const handleRejectBtnClick = () => {
    showRejectionDialog(true);
  };
  const handleRejectCancelBtnClick = () => {
    showRejectionDialog(false);
  };

  const handleDelegateSvuBtnClick = () => {
    showDelegateSvuDialog(true);
  };
  const handleDelegateSvuCancelBtnClick = () => {
    showDelegateSvuDialog(false);
  };

  // ACTIONS
  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  const handleRejectCallback = useMemo(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        return crossOwnerDocsRejectPartialUpdate;
      case FlowEnum.CROSS_WORKER:
        return crossWorkerDocsRejectPartialUpdate;
      case FlowEnum.SVU_OWNER:
        return svuOwnerDocsRejectPartialUpdate;
      case FlowEnum.SVU_WORKER:
        return svuWorkerDocsRejectPartialUpdate;
      case FlowEnum.SVU_CROSS_OWNER:
        return svuCrossOwnerDocsRejectPartialUpdate;
      case FlowEnum.SVU_CROSS_WORKER:
        return svuCrossWorkerDocsRejectPartialUpdate;
      default:
        return dmvsGadDocsRejectPartialUpdate;
    }
  }, [data.flow]);

  const potvrdZapracovatelnostCallback = useMemo(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        return crossOwnerDocsR17PotvrdZapracovatelnostCreate;
      case FlowEnum.SVU_OWNER:
        return svuOwnerDocsR17PotvrdZapracovatelnostCreate;
      case FlowEnum.SVU_WORKER:
        return svuWorkerDocsR17PotvrdZapracovatelnostCreate;
      case FlowEnum.SVU_CROSS_OWNER:
        return svuCrossOwnerDocsR17PotvrdZapracovatelnostCreate;
      case FlowEnum.SVU_CROSS_WORKER:
        return svuCrossWorkerDocsR17PotvrdZapracovatelnostCreate;
      default:
        return dmvsGadDocsR17PotvrdZapracovatelnostCreate;
    }
  }, [data.flow]);

  const handleImportBtnClick = () => {
    switch (data.flow) {
      case FlowEnum.CROSS_WORKER:
        doAction(crossWorkerDocsImportDocCreate, setImportQueryState);
        break;
      case FlowEnum.SVU_WORKER:
        doAction(svuWorkerDocsImportDocCreate, setImportQueryState);
        break;
      case FlowEnum.SVU_CROSS_WORKER:
        doAction(svuCrossWorkerDocsImportDocCreate, setImportQueryState);
        break;
      default:
        doAction(dmvsGadDocsImportDocCreate, setImportQueryState);
        break;
    }
  };

  const handleCheckSvuCrossKrajBtnClick = () => {
    doAction(svuWorkerDocsCheckSvuCrossKrajPartialUpdate, setCheckSvuCrossKrajQueryState);
  };

  const handlePotvrdZapracovatelnostBtnClick = () => {
    const executeAction = () => {
      doAction(potvrdZapracovatelnostCallback, setPotvrdZapracovatelnostQueryState, {
        request: { typ_zapracovatelnosti: 'ZAPRACOVATELNA' },
      });
    };
    const needsConfirmation = data.state !== DOC_STATE.SIGNED_FAILED;

    if (
      !needsConfirmation ||
      window.confirm(formatMessage(docEditMessages.potvrdZapracovatelnostConfirm))
    ) {
      if (data.rejected) {
        handleRejectCallback({
          query: { id: docId },
          request: {
            rejection_reason: data.rejection_reason,
            rejection_note: data.rejection_note,
          },
        });
      } else {
        executeAction();
      }
    }
  };

  const handleDelegateBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.SVU_WORKER:
        doAction(svuWorkerDocsDelegatePartialUpdate, changeCrossDataToDmvsQueryState);
        break;
      case FlowEnum.SVU_OWNER:
        if (data.dmvs_preshranicni_svu && data.dmvs_preshranicni_svu.length > 1) {
          handleDelegateSvuBtnClick();
        } else {
          if (data.dmvs_preshranicni_svu && data.dmvs_preshranicni_svu.length === 1) {
            doAction(svuOwnerDocsDelegatePartialUpdate, changeCrossDataToDmvsQueryState, {
              query: { id: docId, svu_id_subjektu: data.dmvs_preshranicni_svu[0] },
            });
          } else {
            console.error('[DocDmvsGad]: Delegate to SVÚ, but dmvs_preshranicni_svu is empty.');
          }
        }
        break;
      default:
        break;
    }
  }, [data.flow]);

  const handleDelegateCallback = (id: string) => {
    handleDelegateSvuCancelBtnClick();
    // TODO - doAction -> query: { svu_id_subjektu: id }, - zmenit do query a ne request !!!!!
    doAction(svuOwnerDocsDelegatePartialUpdate, changeCrossDataToDmvsQueryState, {
      query: { id: docId, svu_id_subjektu: id },
    });
  };

  const handleCloseBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsClosePartialUpdate, setCloseQueryState);
        break;
      case FlowEnum.CROSS_WORKER:
        doAction(crossWorkerDocsClosePartialUpdate, setCloseQueryState);
        break;
      case FlowEnum.SVU_WORKER:
        doAction(svuWorkerDocsClosePartialUpdate, setCloseQueryState);
        break;
      case FlowEnum.SVU_OWNER:
        doAction(svuOwnerDocsClosePartialUpdate, setCloseQueryState);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doAction(svuCrossOwnerDocsClosePartialUpdate, setCloseQueryState);
        break;
      case FlowEnum.SVU_CROSS_WORKER:
        doAction(svuCrossWorkerDocsClosePartialUpdate, setCloseQueryState);
        break;
      default:
        doAction(dmvsGadDocsClosePartialUpdate, setCloseQueryState);
        break;
    }
  }, [data.flow]);

  const handleCloseInterruptBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsCloseInterruptPartialUpdate, setCloseQueryState);
        break;
      default:
        doAction(dmvsGadDocsCloseInterruptPartialUpdate, setCloseInterruptQueryState);
        break;
    }
  }, [data.flow]);

  const handleImportInterruptBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsImportInterruptPartialUpdate, setImportInterruptQueryState);
        break;
      default:
        doAction(dmvsGadDocsImportInterruptPartialUpdate, setImportInterruptQueryState);
        break;
    }
  }, [data.flow]);

  const handleNotifyCloseBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsNotifyClosePartialUpdate, setNotifyCloseQueryState);
        break;
      default:
        doAction(dmvsGadDocsNotifyClosePartialUpdate, setNotifyCloseQueryState);
        break;
    }
  }, [data.flow]);

  const handleEditBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsEditPartialUpdate, setCrossEditQueryState);
        break;
      case FlowEnum.SVU_OWNER:
        doAction(svuOwnerDocsEditPartialUpdate, setEditQueryState);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doAction(svuCrossOwnerDocsEditPartialUpdate, setEditQueryState);
        break;
      default:
        doAction(dmvsGadDocsEditPartialUpdate, setEditQueryState);
        break;
    }
  }, [data.flow]);

  const handleSendToCounterpartBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doSendToCounterpart(crossOwnerDocsSendToCounterpartPartialUpdate);
        break;
      case FlowEnum.CROSS_WORKER:
        doSendToCounterpart(crossWorkerDocsSendToCounterpartPartialUpdate);
        break;
      case FlowEnum.SVU_OWNER:
        doSendToCounterpart(svuOwnerDocsSendToCounterpartPartialUpdate);
        break;
      case FlowEnum.SVU_WORKER:
        doSendToCounterpart(svuWorkerDocsSendToCounterpartPartialUpdate);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doSendToCounterpart(svuCrossOwnerDocsSendToCounterpartPartialUpdate);
        break;
      case FlowEnum.SVU_CROSS_WORKER:
        doSendToCounterpart(svuCrossWorkerDocsSendToCounterpartPartialUpdate);
        break;
    }
  }, [data.flow]);

  const handleCloseCrossDataToDmvsBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doSendCloseData(crossOwnerDocsSendCloseDataToDmvsPartialUpdate);
        break;
      case FlowEnum.SVU_OWNER:
        doSendCloseData(svuOwnerDocsSendCloseDataToDmvsPartialUpdate);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doSendCloseData(svuCrossOwnerDocsSendCloseDataToDmvsPartialUpdate);
        break;
      default:
        break;
    }
  }, [data.flow]);

  const handleEditCheckBtnClick = useCallback(() => {
    // Spuštěná kontrola je odpověď na odmítnuté odeslání, takže jeho hláška ztrácí platnost.
    changeCrossDataToDmvsQueryState(QUERY_STATE.EMPTY);
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsEditCheckCreate, setCheckQueryState);
        break;
      default:
        doAction(dmvsGadDocsEditCheckCreate, setCheckQueryState);
        break;
    }
  }, [data.flow]);

  // Synchronization
  const handleSynchronizeBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsSynchronizeCreate, setSynchronizationQueryState);
        break;
      case FlowEnum.CROSS_WORKER:
        doAction(crossWorkerDocsSynchronizeCreate, setSynchronizationQueryState);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doAction(svuCrossOwnerDocsSynchronizeCreate, setSynchronizationQueryState);
        break;
      default:
        break;
    }
  }, [data.flow]);

  const handleRequestSynchronizationDataBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(crossOwnerDocsRequestSynchronizationDataCreate, setSynchronizationQueryState);
        break;
      case FlowEnum.CROSS_WORKER:
        doAction(crossWorkerDocsRequestSynchronizationDataCreate, setSynchronizationQueryState);
        break;
      case FlowEnum.SVU_CROSS_OWNER:
        doAction(svuCrossOwnerDocsRequestSynchronizationDataCreate, setSynchronizationQueryState);
        break;
      case FlowEnum.SVU_CROSS_WORKER:
        doAction(svuCrossWorkerDocsRequestSynchronizationDataCreate, setSynchronizationQueryState);
        break;
      default:
        break;
    }
  }, [data.flow]);

  const handleCheckDmvsPendingPozadavkyBtnClick = useCallback(() => {
    switch (data.flow) {
      case FlowEnum.CROSS_OWNER:
        doAction(
          crossOwnerDocsCheckDmvsPendingPozadavkyCreate,
          setCheckDmvsPendingPozadavkyQueryState,
        );
        break;
      default:
        doAction(
          dmvsGadDocsCheckDmvsPendingPozadavkyCreate,
          setCheckDmvsPendingPozadavkyQueryState,
        );
        break;
    }
  }, [data.flow]);

  const markTimelineStale = timeline.markStale;

  // Neúspěšná akce nezmění stav dokumentu, takže SSE nepřijde a osa by zůstala bez nového záznamu.
  const handleRefetch = useCallback(() => {
    resource.refetch();
    markTimelineStale();
  }, [markTimelineStale, resource]);

  const actionQuery = useMemo<DocActionQuery>(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: handleRefetch,
  });

  // Chyby patří ke stejnému alertu jako crossDataToDmvsQueryState, proto se zahazují s ním.
  const changeCrossDataToDmvsQueryState = useCallback((state: SetStateAction<QUERY_STATE>) => {
    setSendToCounterpartError(null);
    setSendCloseDataErrors(null);
    setCrossDataToDmvsQueryState(state);
  }, []);

  const doSendToCounterpart = useCallback(
    async (action: ApiAction<{ id?: number }>) => {
      const response = await doAction(action, changeCrossDataToDmvsQueryState);
      if (response && !response.ok) {
        setSendToCounterpartError(response.error?.data ?? null);
      }
    },
    [changeCrossDataToDmvsQueryState, doAction],
  );

  const doSendCloseData = useCallback(
    async (action: ApiAction<{ id?: number }>) => {
      const response = await doAction(action, changeCrossDataToDmvsQueryState);
      if (response && !response.ok) {
        setSendCloseDataErrors(getErrorMessages(response.error?.data));
      }
    },
    [changeCrossDataToDmvsQueryState, doAction],
  );

  const handleSave = useCallback(async () => {
    const formValues = getFormValues({
      editor_manager,
    });
    const request = {
      editor_manager: formValues.editor_manager?.url || '',
    };

    doAction(dmvsGadDocsPartialUpdate, setSaveQueryState, { request });
  }, [doAction, editor_manager]);

  const handleCheckClick = useCallback((id: number) => {
    setCheckLogId(id);
    showLogDialog(true);
  }, []);

  const handleCancelLogDialogBtnClick = useCallback(() => {
    setCheckLogId(undefined);
    showLogDialog(false);
  }, []);

  const handleCuzkIdPodaniClick = useCallback(() => {
    const url = `https://dmvs.cuzk.gov.cz/portal/gad/stav-zpracovani?id=${dmvs_id_podani.value}`;
    window.open(url, '_blank');
  }, [dmvs_id_podani.value]);

  const isCloseBtn =
    !!docId &&
    acl.docsActionClose &&
    (isUserEditor ||
      data.state === DOC_STATE.CLOSE_FAILED ||
      data.state === DOC_STATE.READY_TO_PROCESS ||
      data.state === DOC_STATE.PROCESS_FAILED);

  const sendCrossDataBtnLabel = useMemo(() => {
    switch (data.state) {
      case DOC_STATE.SEND_TO_COUNTERPART_FAILED:
        return docEditButtonsMsg.docBtnRetry;
      case DOC_STATE.EDITING:
        if (data.flow === FlowEnum.CROSS_OWNER) {
          return docEditButtonsMsg.docBtnSendCrossDataToDmvs;
        }
        return docEditButtonsMsg.docBtnSendCrossDataToSvu;
      default:
        return docEditButtonsMsg.docBtnSendCrossDataToDmvs;
    }
  }, [data.state]);

  const isOwner = data.flow && (OWNER_FLOWS as string[]).includes(data.flow);
  const isWorker = data.flow && (WORKER_FLOWS as string[]).includes(data.flow);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div
            className={classNames({
              'dmx-doc--head sg-u-vs-2': true,
              'dmx-is-owner': isOwner,
              'dmx-is-worker': isWorker,
            })}
          >
            {/* className='dmx-doc--head sg-u-vs-2' */}
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                {data.dmvs_source === 'R16PrijmiGadDtmk' && (
                  <LinkTooltip
                    title={`${formatMessage(docEditButtonsMsg.docBtnLinkToDmvsGad)} DTMK KAD`}
                    description={`id: ${data.dmvs_identifikace_zmeny}`}
                    onClick={handleLinkToDtmkKadBtnClick}
                  />
                )}
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, formType, {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
                {(isOwner || isWorker) &&
                  data.svu_cross_names?.map((item) => <InfoBadge key={item}>{item}</InfoBadge>)}
              </header>
              <BtnBar>
                {acl.docsUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSave}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionEditCheck && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleEditCheckBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnEditCheck} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionNotifyClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(notifyCloseQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleNotifyCloseBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCloseInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(closeInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionImportInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(importInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionEdit && (
                  <SecondaryBtn
                    queryState={
                      toBtnQueryState(editQueryState) ?? toBtnQueryState(crossEditQueryState)
                    }
                    isDisabled={isActionRunning}
                    onClick={handleEditBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnEdit} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionSendToCounterpart && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(crossDataToDmvsQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSendToCounterpartBtnClick}
                  >
                    <FormattedMessage {...sendCrossDataBtnLabel} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCloseCrossDataToDmvs && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(crossDataToDmvsQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseCrossDataToDmvsBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCloseCrossDataToDmvs} />
                  </SecondaryBtn>
                )}
                {!!docId && acl.docsActionCheckSvuCrossKraj && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkSvuCrossKrajQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckSvuCrossKrajBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckSvuCrossKraj} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionPotvrdZapracovatelnost && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(potvrdZapracovatelnostQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handlePotvrdZapracovatelnostBtnClick}
                  >
                    {data.state === DOC_STATE.SIGNED_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetryPotvrdZapracovatelnost} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnPotvrdZapracovatelnost} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionDelegate && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(crossDataToDmvsQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleDelegateBtnClick}
                  >
                    {data.state === DOC_STATE.DELEGATE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnDelegate} />
                    )}
                  </SecondaryBtn>
                )}

                {isCloseBtn && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSE_INTERRUPTED ||
                    data.state === DOC_STATE.CLOSE_FAILED ||
                    data.state === DOC_STATE.NOTIFYING_CLOSE_FAILED ||
                    data.state === DOC_STATE.PROCESS_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnClose} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId &&
                  acl.docsActionImport &&
                  (data.state === DOC_STATE.IMPORT_INTERRUPTED ||
                  data.state === DOC_STATE.IMPORTED_WITH_ERRORS ||
                  data.state === DOC_STATE.IMPORT_FAILED ? (
                    <SecondaryBtn
                      queryState={toBtnQueryState(importQueryState)}
                      isDisabled={isActionRunning}
                      onClick={handleImportBtnClick}
                    >
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    </SecondaryBtn>
                  ) : (
                    <CtaBtn
                      queryState={toBtnQueryState(importQueryState)}
                      isDisabled={isActionRunning}
                      onClick={handleImportBtnClick}
                    >
                      <FormattedMessage {...docEditButtonsMsg.docBtnImport} />
                    </CtaBtn>
                  ))}
                {!!docId && acl.docsActionReject && (
                  <SecondaryBtn
                    isDanger={data.state === DOC_STATE.WAITING_FOR_REJECT_FAILED ? undefined : true}
                    isDisabled={isActionRunning}
                    onClick={handleRejectBtnClick}
                  >
                    {data.state === DOC_STATE.WAITING_FOR_REJECT_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnReject} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionSynchronize && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(synchronizationQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSynchronizeBtnClick}
                  >
                    {data.state === DOC_STATE.SYNCHRONIZE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnSynchronize} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionRequestSynchronizationData && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(synchronizationQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleRequestSynchronizationDataBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRequestSynchronizationData} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCheckDmvsPendingPozadavky && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkDmvsPendingPozadavkyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckDmvsPendingPozadavkyBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckDmvsPendingPozadavky} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              checkQueryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              importQueryState === QUERY_STATE.WAITING ||
              checkSvuCrossKrajQueryState === QUERY_STATE.WAITING ||
              potvrdZapracovatelnostQueryState === QUERY_STATE.WAITING ||
              closeInterruptQueryState === QUERY_STATE.WAITING ||
              importInterruptQueryState === QUERY_STATE.WAITING ||
              editQueryState === QUERY_STATE.WAITING ||
              crossEditQueryState === QUERY_STATE.WAITING ||
              crossDataToDmvsQueryState === QUERY_STATE.WAITING ||
              synchronizationQueryState === QUERY_STATE.WAITING ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.WAITING ||
              notifyCloseQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(editQueryState === QUERY_STATE.FAIL ||
              crossEditQueryState === QUERY_STATE.FAIL ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.queryStateFail} />
              </DangerAlert>
            )}
            {saveQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {(closeQueryState === QUERY_STATE.FAIL ||
              notifyCloseQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
            {importQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.importQueryStateFail} />
              </DangerAlert>
            )}
            {checkSvuCrossKrajQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.checkSvuCrossKrajQueryStateFail} />
              </DangerAlert>
            )}
            {potvrdZapracovatelnostQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.potvrdZapracovatelnostQueryStateFail} />
              </DangerAlert>
            )}
            {checkQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.checkQueryStateFail} />
              </DangerAlert>
            )}
            {(closeInterruptQueryState === QUERY_STATE.FAIL ||
              importInterruptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.interruptQueryStateFail} />
              </DangerAlert>
            )}
            {synchronizationQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.synchronizationQueryStateFail} />
              </DangerAlert>
            )}
            {crossDataToDmvsQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                {sendCloseDataErrors ? (
                  sendCloseDataErrors.map((error, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <br />}
                      <ResponseErrorMessage error={error} />
                    </React.Fragment>
                  ))
                ) : (
                  <FormattedMessage
                    {...getSendToCounterpartFailMessage(sendToCounterpartError?.code)}
                  />
                )}
              </DangerAlert>
            )}

            {(
              [
                DOC_STATE.REJECTED,
                DOC_STATE.EDIT_REJECTED,
                DOC_STATE.EDIT_TAKEOVER_REJECTED,
              ] as DocStatesEnum[]
            ).includes(data.state) &&
              resource.queryState === QUERY_STATE.SUCCESS &&
              data.rejection_reason && (
                <WarningAlert>
                  <b className='sg-u-type-bold'>
                    <FormattedMessage {...rejectionMessages.reasonTitle} />
                    {' - '}
                    <FormattedMessage
                      {...rejectionReasonMessages[data.rejection_reason ?? 'null']}
                    />
                  </b>
                  {data.rejection_note && (
                    <p className='dmx-doc--warningAlertText sg-a-mt-1/2'>{data.rejection_note}</p>
                  )}
                </WarningAlert>
              )}

            {isActionMapEditVisible && !isUserEditor && (
              <WarningAlert>
                <FormattedMessage {...docEditMessages.canNotEdit} />
              </WarningAlert>
            )}
            {linkToDtmkKadWarningShow && (
              <WarningAlert>
                <FormattedMessage {...docEditMessages.linkToDtmkKadWarning} />
              </WarningAlert>
            )}
            {data.flow === FlowEnum.SVU_OWNER &&
              data.state === DOC_STATE.SEND_TO_COUNTERPART_FAILED && (
                <LogAlert log={timeline.latestError ?? undefined} />
              )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <div className='sg-u-vs-4'>
                <section>
                  <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                  <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                    {usableItems?.includes('dmvs_id_podani') && (
                      <FormRow>
                        <FormItem {...dmvs_id_podani}>
                          <Input {...dmvs_id_podani}>
                            {dmvs_id_podani.value && (
                              <TertiaryBtn
                                icon={{ element: <SvgExternal /> }}
                                tooltip={formatMessage(docFormMessages.dmvs_id_podani_link)}
                                onClick={handleCuzkIdPodaniClick}
                                size='sml'
                              />
                            )}
                          </Input>
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_source') && (
                      <FormRow>
                        <FormItem {...dmvs_source}>
                          <Input {...dmvs_source} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('azi_cislo_opravneni') &&
                      data.dmvs_source !== 'R16PrijmiGadVsp' && (
                        <FormRow>
                          <FormItem {...azi_cislo_opravneni}>
                            <Input {...azi_cislo_opravneni} />
                          </FormItem>
                        </FormRow>
                      )}
                    {usableItems?.includes('subjekt') && data.dmvs_source === 'R16PrijmiGadVsp' && (
                      <>
                        <FormRow>
                          <FormItem {...subjekt_jmeno}>
                            <Input {...subjekt_jmeno} />
                          </FormItem>
                        </FormRow>
                        <FormRow>
                          <FormItem {...geodesist}>
                            <Input {...geodesist} />
                          </FormItem>
                        </FormRow>
                      </>
                    )}
                    {usableItems?.includes('description') && (
                      <FormRow>
                        <FormItem {...description}>
                          <Textarea {...description} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_identifikace_zmeny') && (
                      <FormRow>
                        <FormItem {...dmvs_identifikace_zmeny}>
                          <Input {...dmvs_identifikace_zmeny} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('editor_manager') && acl.docsUpdateEditorManager && (
                      <FormRow>
                        <FormItem {...editor_manager}>
                          <CustomSelect {...editor_manager} />
                        </FormItem>
                      </FormRow>
                    )}
                  </div>
                </section>
              </div>
              {resource.data && <DocInternalInfo doc={resource.data} />}
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  {usableItems?.includes('attachments') && (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.attachments_docs} />
                      </h3>
                      <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                        <DocAttachments
                          doc={resource?.data}
                          prejimkaType={data.prejimka_type}
                          attachments={stateData.attachments}
                        />
                      </div>
                    </section>
                  )}
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDownloadCloseLog,
                          link: closeLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadCloseLog} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadDmvsLog,
                          link: dmvsLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLogDmvs} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadImportLog,
                          link: importLogDownloadLink,
                          label: (
                            <FormattedMessage {...docEditButtonsMsg.docBtnDownloadImportLog} />
                          ),
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadProtocol,
                          link: protocolDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadProtocol} />,
                          ext: 'pdf',
                        },
                        {
                          permission: acl.docsActionDownloadSynchronizedLog,
                          link: logSynchronizedDonwloadLink,
                          label: (
                            <FormattedMessage
                              {...docEditButtonsMsg.docBtnDownloadSynchronizedLog}
                            />
                          ),
                          ext: 'zip',
                        },
                        {
                          permission:
                            !!data.digitalni_osvedceni_signed ||
                            !!data.digitalni_osvedceni_unsigned,
                          link: digitalniOsvedceniDownloadLink,
                          label: (
                            <FormattedMessage
                              {...docEditButtonsMsg.docBtnDownloadDigitalniOsvedceni}
                            />
                          ),
                          ext: 'pdf',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>
            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {(acl.docsActionMapReview || acl.docsActionMapEdit) && mapState && (
                <section className='sg-a-f-a sg-a-d-f sg-a-fd-c sg-a-rg-1'>
                  <div className='sg-a-d-f sg-a-jc-sb sg-a-ai-c sg-a-fw-w sg-a-cg-2'>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.docAreaTitle} />
                    </h3>
                    <BtnBar>
                      {isActionMapEditVisible && isUserEditor && editorUrl && (
                        <SecondaryLink
                          href={editorUrl}
                          target='_blank'
                          icon={{ element: <SvgPen /> }}
                        >
                          <FormattedMessage {...docEditButtonsMsg.docBtnMapEditShort} />
                        </SecondaryLink>
                      )}
                      {canMapReview && (
                        <MapReviewButtons reviewUrl={reviewUrl} review3dUrl={review3dUrl} />
                      )}
                    </BtnBar>
                  </div>
                  <div className='sg-u-box sg-a-p-2 sg-a-f-a'>
                    <MapWrapper>
                      <MapWithAreas state={mapState} extent={mapExtent} />
                    </MapWrapper>
                  </div>
                </section>
              )}

              {resource.data?.checks && resource.data.checks.length > 0 && showChecks && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>
                    <FormattedMessage {...docFormMessages.checks} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                    <CheckRecordList
                      docId={docId}
                      items={resource.data.checks}
                      onClick={handleCheckClick}
                      onRefetch={handleRefetch}
                    />
                  </div>
                </section>
              )}
              {acl.docsActionShowLog && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}

              {hasHistoryGraph ? (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.processHistory} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                    <HistoryGraph timeline={timeline} history={data.history} flow={data.flow} />
                  </div>
                </section>
              ) : (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.history} />
                  </h3>
                  <HistoryList history={data.history} />
                </section>
              )}
            </div>
          </div>

          {isLogDialogShown && checkLogId && (
            <Dialog
              onEscKeyDown={handleCancelLogDialogBtnClick}
              onOverlayClick={handleCancelLogDialogBtnClick}
            >
              <CloseDialogBtn onClick={handleCancelLogDialogBtnClick} />
              <div className='sg-a-p-4 sg-u-vs-2'>
                <PageTitle>
                  <FormattedMessage {...docFormMessages.logs} />
                </PageTitle>
                <LogList objectId={checkLogId} resourceType={'check'} />
              </div>
            </Dialog>
          )}

          {isRejectionDialogShown && !!docId && (
            <Dialog width='500px'>
              <Rejection
                docId={docId}
                prejimkaType={data.prejimka_type}
                onCancelBtnClick={handleRejectCancelBtnClick}
                resource={resource}
                partialUpdateCallback={handleRejectCallback}
              />
            </Dialog>
          )}

          {isDelegateSvuDialogShown &&
            !!docId &&
            data.dmvs_preshranicni_svu &&
            data.dmvs_preshranicni_svu.length > 1 && (
              <Dialog width='500px'>
                <Delegate
                  idSubjektu={data.dmvs_preshranicni_svu ?? []}
                  onCancelBtnClick={handleDelegateSvuCancelBtnClick}
                  partialUpdateCallback={handleDelegateCallback}
                />
              </Dialog>
            )}
        </article>
      )}
    </MainContent>
  );
};
